import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../services/AuthContext";
import api from "../services/api";
import {
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  TextField,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import logo from "../assets/logo-white.png";
import heroImage from "../assets/heroimage1.png";
import UserSettingsStyles from "../styles/UserSettingsStyles";

const {
  container,
  appBar,
  toolbar,
  logo: logoStyle,
  navButtonsContainer,
  navButton,
  logoutButton,
  heroContent,
  heroTitle,
  contentContainer,
  mainContentBox,
  formBox,
  formTitle,
  textField,
  errorText,
  actionButton,
  footer,
  footerText,
  footerButton,
  dialogTitle,
  dialogContentText,
  dialogActionButton,
} = UserSettingsStyles;

function UserSettings() {
  const { user, logout } = useAuthContext();
  const [formValues, setFormValues] = useState({
    username: "",
    email: "",
    companyName: "",
    address: "",
    country: "",
    contactNo: "",
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [error, setError] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setFormValues({
        username: user.username || "",
        email: user.email || "",
        companyName: user.company_name || "",
        address: user.address || "",
        country: user.country || "",
        contactNo: user.contact_no || "",
        oldPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    } else {
      fetchUserDetails();
    }
  }, [user]);

  const fetchUserDetails = () => {
    const token = localStorage.getItem("token");
    api
      .get("/auth/user/profile", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setFormValues({
          username: response.data.username,
          email: response.data.email,
          companyName: response.data.company_name || "",
          address: response.data.address || "",
          country: response.data.country || "",
          contactNo: response.data.contact_no || "",
          oldPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
      })
      .catch((error) => {
        console.error("Failed to fetch user details", error.response?.data?.detail || error.message);
        setError(error.response?.data?.detail || "Failed to fetch user details.");
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
    setError(null);
  };

  const handleUpdateProfile = async () => {
    setIsLoadingProfile(true);
    setError(null);
    if (!formValues.username || !formValues.email) {
      setError("Username and email are required.");
      setIsLoadingProfile(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await api.put(
        "/auth/user/profile",
        {
          username: formValues.username,
          email: formValues.email,
          company_name: formValues.companyName,
          address: formValues.address,
          country: formValues.country,
          contact_no: formValues.contactNo,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showSnackbar("Profile updated successfully.");
      fetchUserDetails();
    } catch (error) {
      console.error("Failed to update profile", error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || "Failed to update profile. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setIsLoadingPassword(true);
    setError(null);
    if (formValues.newPassword !== formValues.confirmNewPassword) {
      setError("New passwords do not match.");
      setIsLoadingPassword(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await api.put(
        "/auth/user/change-password",
        {
          old_password: formValues.oldPassword,
          new_password: formValues.newPassword,
          confirm_new_password: formValues.confirmNewPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showSnackbar("Password changed successfully.");
      setFormValues({ ...formValues, oldPassword: "", newPassword: "", confirmNewPassword: "" });
    } catch (error) {
      console.error("Failed to change password", error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || "Failed to change password.";
      setError(errorMessage);
      showSnackbar(errorMessage);
    } finally {
      setIsLoadingPassword(false);
    }
  };

  const handleLogoutConfirm = () => {
    setShowConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  return (
    <Box sx={container}>
      <AppBar position="sticky" sx={appBar(heroImage)}>
        <Toolbar sx={toolbar}>
          <img src={logo} alt="Logo" style={logoStyle} />
          <Box sx={navButtonsContainer}>
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard?view=labelClaims")}
              sx={navButton}
            >
              Label Claims
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard?view=orderDetails")}
              sx={navButton}
            >
              Order Details
            </Button>
            <Button color="inherit" onClick={() => navigate("/user-settings")} sx={navButton}>
              User Settings
            </Button>
          </Box>
          <Button color="inherit" onClick={handleLogoutConfirm} sx={logoutButton}>
            Logout
          </Button>
        </Toolbar>
        <Box sx={heroContent}>
          <Typography variant="h3" sx={heroTitle}>
            User Settings
          </Typography>
        </Box>
      </AppBar>
      <Grid container sx={contentContainer}>
        <Grid item xs={12}>
          <Box sx={mainContentBox}>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} md={6}>
                <Box sx={formBox}>
                  <Typography variant="h6" sx={formTitle}>
                    Update Profile
                  </Typography>
                  {error && (
                    <Typography variant="body2" color="error" sx={errorText}>
                      {error}
                    </Typography>
                  )}
                  <TextField
                    label="Username"
                    variant="outlined"
                    fullWidth
                    name="username"
                    value={formValues.username}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    name="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <TextField
                    label="Company Name"
                    variant="outlined"
                    fullWidth
                    name="companyName"
                    value={formValues.companyName}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <TextField
                    label="Address"
                    variant="outlined"
                    fullWidth
                    name="address"
                    value={formValues.address}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <TextField
                    label="Country"
                    variant="outlined"
                    fullWidth
                    name="country"
                    value={formValues.country}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <TextField
                    label="Contact No"
                    variant="outlined"
                    fullWidth
                    name="contactNo"
                    value={formValues.contactNo}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingProfile}
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleUpdateProfile}
                    sx={actionButton}
                    disabled={isLoadingProfile}
                  >
                    {isLoadingProfile ? "Updating..." : "Update Profile"}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={formBox}>
                  <Typography variant="h6" sx={formTitle}>
                    Change Password
                  </Typography>
                  {error && (
                    <Typography variant="body2" color="error" sx={errorText}>
                      {error}
                    </Typography>
                  )}
                  <TextField
                    label="Old Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    name="oldPassword"
                    value={formValues.oldPassword}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingPassword}
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    name="newPassword"
                    value={formValues.newPassword}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingPassword}
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    name="confirmNewPassword"
                    value={formValues.confirmNewPassword}
                    onChange={handleInputChange}
                    sx={textField}
                    disabled={isLoadingPassword}
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleChangePassword}
                    sx={actionButton}
                    disabled={isLoadingPassword}
                  >
                    {isLoadingPassword ? "Changing..." : "Change Password"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              message={snackbarMessage}
              onClose={() => setSnackbarOpen(false)}
            />
          </Box>
        </Grid>
      </Grid>
      <Box sx={footer}>
        <Typography variant="body2" sx={footerText}>
          © 2025 DealSphere. All rights reserved.
        </Typography>
        <Box>
          {['Terms', 'Privacy', 'Contact'].map((text) => (
            <Button key={text} sx={footerButton}>
              {text}
            </Button>
          ))}
        </Box>
      </Box>
      <Dialog open={showConfirmDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={dialogTitle}>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography sx={dialogContentText}>
            Are you sure you want to log out?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={dialogActionButton}>
            Cancel
          </Button>
          <Button onClick={handleLogout} sx={dialogActionButton}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserSettings;